import React, { useEffect, useState } from 'react';
import { Plus, UserCog, X, ShieldCheck, ShieldOff } from 'lucide-react';
import { api, formatError, fmtDate } from '../api';

export default function Team() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [err, setErr] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch (e) { setErr(formatError(e)); }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      reload();
    } catch (e) { setErr(formatError(e)); }
  };

  return (
    <div data-testid="crm-team">
      <div className="crm-card" style={{ marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>Team & Roles</h3>
          <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: '4px 0 0' }}>
            Create accounts for sales reps, estimators and other admins. Assigned roles control what each user can do.
          </p>
        </div>
        <button className="crm-btn" onClick={() => setShowNew(true)} data-testid="team-new-user-btn"><Plus size={16} /> Add Team Member</button>
      </div>

      {err && <div className="crm-card" style={{ color: '#9f1239', marginBottom: 12 }}>{err}</div>}

      <div className="crm-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table className="crm-table" style={{ minWidth: 720 }}>
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th><th></th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--crm-muted)' }}>Loading…</td></tr>
              : users.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--crm-muted)' }}>No team members yet.</td></tr>
                : users.map((u) => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 700 }}>{u.name}</td>
                    <td style={{ color: 'var(--crm-muted)' }}>{u.email}</td>
                    <td>
                      <span className="crm-pill" style={{
                        background: u.role === 'admin' ? '#fee2e2' : u.role === 'sales' ? '#dbeafe' : '#d1fae5',
                        color: u.role === 'admin' ? '#9f1239' : u.role === 'sales' ? '#1e40af' : '#065f46',
                      }}><UserCog size={11} /> {u.role}</span>
                    </td>
                    <td>{u.active ? <span style={{ color: 'var(--crm-emerald)', fontWeight: 700 }}>Active</span> : <span style={{ color: 'var(--crm-rose)', fontWeight: 700 }}>Disabled</span>}</td>
                    <td style={{ color: 'var(--crm-muted)' }}>{fmtDate(u.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="crm-btn ghost sm" onClick={() => setEditUser(u)}>Edit</button>{' '}
                      <button className={`crm-btn sm ${u.active ? 'danger' : 'success'}`} onClick={() => toggleActive(u)}>
                        {u.active ? <><ShieldOff size={12} /> Disable</> : <><ShieldCheck size={12} /> Enable</>}
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {showNew && <UserModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
      {editUser && <UserModal user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); reload(); }} />}
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const editing = Boolean(user);
  const [form, setForm] = useState({
    name: user?.name || '', email: user?.email || '', role: user?.role || 'sales', password: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      if (editing) {
        const body = { name: form.name, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${user.id}`, body);
      } else {
        await api.post('/users', { name: form.name, email: form.email, role: form.role, password: form.password });
      }
      onSaved?.();
    } catch (e) { setErr(formatError(e)); }
    setBusy(false);
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal" onClick={(e) => e.stopPropagation()} data-testid="crm-user-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <h2>{editing ? `Edit ${user.name}` : 'Add Team Member'}</h2>
          <button onClick={onClose} className="crm-btn ghost sm"><X size={14} /></button>
        </div>
        {err && <div style={{ background: 'var(--crm-rose-soft)', color: '#9f1239', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <form onSubmit={submit}>
          <div className="crm-grid-2">
            <div><label className="crm-label">Full name *</label><input className="crm-input" required value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div>
              <label className="crm-label">Role</label>
              <select className="crm-select" value={form.role} onChange={(e) => set('role', e.target.value)}>
                <option value="sales">Sales Rep</option>
                <option value="estimator">Estimator</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label className="crm-label">Email *</label>
            <input type="email" required disabled={editing} className="crm-input" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div style={{ marginTop: 10 }}>
            <label className="crm-label">{editing ? 'Reset password (leave blank to keep current)' : 'Initial password *'}</label>
            <input type="text" required={!editing} className="crm-input" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder={editing ? 'Leave blank to keep' : 'Min 6 chars'} />
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} className="crm-btn ghost">Cancel</button>
            <button type="submit" className="crm-btn" disabled={busy} data-testid="user-modal-submit">{busy ? 'Saving…' : (editing ? 'Save changes' : 'Add member')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
